<template>
  <MainLayout>
    <div class="leaderboard-content">
      <div class="d-flex align-center mb-6">
        <v-select
          v-model="selectedPeriod"
          :items="periodOptions"
          item-title="label"
          item-value="value"
          label="Leaderboard Period"
          variant="outlined"
          density="comfortable"
          class="mr-4"
          style="max-width: 220px;"
        />
        <span class="text-h5 font-weight-bold ml-2">
          {{ leaderboardTitle }}
        </span>
        
        <!-- Month Navigation Controls (only for Monthly) -->
        <div v-if="selectedPeriod === 'monthly'" class="d-flex align-center ml-4">
          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            size="small"
            :disabled="!canGoPreviousMonth"
            @click="goToPreviousMonth"
            class="mr-2"
          />
          <v-btn
            variant="outlined"
            size="small"
            @click="goToCurrentMonth"
            class="mr-2"
          >
            Current
          </v-btn>
          <v-btn
            icon="mdi-chevron-right"
            variant="text"
            size="small"
            :disabled="!canGoNextMonth"
            @click="goToNextMonth"
          />
        </div>
        
        <!-- Year Navigation Controls (only for Yearly) -->
        <div v-if="selectedPeriod === 'yearly'" class="d-flex align-center ml-4">
          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            size="small"
            :disabled="!canGoPreviousYear"
            @click="goToPreviousYear"
            class="mr-2"
          />
          <v-btn
            variant="outlined"
            size="small"
            @click="goToCurrentYear"
            class="mr-2"
          >
            Current
          </v-btn>
          <v-btn
            icon="mdi-chevron-right"
            variant="text"
            size="small"
            :disabled="!canGoNextYear"
            @click="goToNextYear"
          />
        </div>
      </div>
      <v-card elevation="2">
        <v-card-text>
          <div v-if="loading[selectedPeriod]" class="d-flex justify-center align-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-alert
            v-else-if="error[selectedPeriod]"
            type="error"
            class="my-4"
          >
            {{ error[selectedPeriod] }}
          </v-alert>
          <div v-else>
            <div v-if="leaderboards[selectedPeriod].length === 0" class="d-flex flex-column align-center justify-center py-12">
              <v-icon size="48" color="grey-lighten-1" class="mb-4">mdi-trophy-outline</v-icon>
              <div class="text-h6 text-medium-emphasis mb-2">No data available</div>
              <div class="text-body-2 text-grey">
                {{ selectedPeriod === 'monthly' ? `No leaderboard data for ${monthNames[selectedMonth]} ${selectedYear}` : 'No leaderboard data available' }}
              </div>
            </div>
            <LeaderboardTable v-else :entries="leaderboards[selectedPeriod]" />
          </div>
        </v-card-text>
      </v-card>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import MainLayout from './components/MainLayout.vue'
import LeaderboardTable from './components/LeaderboardTable.vue'

interface LeaderboardEntry {
  rank: number
  name: string
  points: number
  slackId: string
}

interface LeaderboardState {
  [key: string]: LeaderboardEntry[]
}

interface LoadingState {
  [key: string]: boolean
}

interface ErrorState {
  [key: string]: string
}

const periodOptions = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
  { label: 'All Time', value: 'allTime' }
]
const selectedPeriod = ref('monthly')

const now = new Date()
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'
] as const

// Track selected month/year for monthly view
const selectedMonth = ref(now.getMonth())
const selectedYear = ref(now.getFullYear())

// Track selected year for yearly view
const selectedYearly = ref(now.getFullYear())

const currentMonth = monthNames[now.getMonth()]
const currentYear = now.getFullYear()

const leaderboardTitle = computed(() => {
  if (selectedPeriod.value === 'monthly') {
    const monthName = monthNames[selectedMonth.value]
    return `Monthly Leaders – ${monthName} ${selectedYear.value}`
  } else if (selectedPeriod.value === 'yearly') {
    return `Yearly Leaders – ${selectedYearly.value}`
  } else {
    return 'All Time Leaders'
  }
})

const canGoPreviousMonth = computed(() => {
  if (selectedPeriod.value !== 'monthly') return false
  return selectedYear.value > 2020 || (selectedYear.value === 2020 && selectedMonth.value > 0)
})

const canGoNextMonth = computed(() => {
  if (selectedPeriod.value !== 'monthly') return false
  const currentDate = new Date()
  return selectedYear.value < currentDate.getFullYear() || 
         (selectedYear.value === currentDate.getFullYear() && selectedMonth.value < currentDate.getMonth())
})

const canGoPreviousYear = computed(() => {
  if (selectedPeriod.value !== 'yearly') return false
  return selectedYearly.value > 2020
})

const canGoNextYear = computed(() => {
  if (selectedPeriod.value !== 'yearly') return false
  const currentDate = new Date()
  return selectedYearly.value < currentDate.getFullYear()
})

async function goToPreviousMonth() {
  if (selectedMonth.value === 0) {
    selectedMonth.value = 11
    selectedYear.value--
  } else {
    selectedMonth.value--
  }
  try {
    loading.value.monthly = true
    error.value.monthly = ''
    leaderboards.value.monthly = await fetchLeaderboard('monthly')
  } catch (e) {
    error.value.monthly = 'Failed to load leaderboard'
  } finally {
    loading.value.monthly = false
  }
}

async function goToNextMonth() {
  if (selectedMonth.value === 11) {
    selectedMonth.value = 0
    selectedYear.value++
  } else {
    selectedMonth.value++
  }
  try {
    loading.value.monthly = true
    error.value.monthly = ''
    leaderboards.value.monthly = await fetchLeaderboard('monthly')
  } catch (e) {
    error.value.monthly = 'Failed to load leaderboard'
  } finally {
    loading.value.monthly = false
  }
}

async function goToCurrentMonth() {
  selectedMonth.value = now.getMonth()
  selectedYear.value = now.getFullYear()
  try {
    loading.value.monthly = true
    error.value.monthly = ''
    leaderboards.value.monthly = await fetchLeaderboard('monthly')
  } catch (e) {
    error.value.monthly = 'Failed to load leaderboard'
  } finally {
    loading.value.monthly = false
  }
}

async function goToPreviousYear() {
  selectedYearly.value--
  try {
    loading.value.yearly = true
    error.value.yearly = ''
    leaderboards.value.yearly = await fetchLeaderboard('yearly')
  } catch (e) {
    error.value.yearly = 'Failed to load leaderboard'
  } finally {
    loading.value.yearly = false
  }
}

async function goToNextYear() {
  selectedYearly.value++
  try {
    loading.value.yearly = true
    error.value.yearly = ''
    leaderboards.value.yearly = await fetchLeaderboard('yearly')
  } catch (e) {
    error.value.yearly = 'Failed to load leaderboard'
  } finally {
    loading.value.yearly = false
  }
}

async function goToCurrentYear() {
  selectedYearly.value = now.getFullYear()
  try {
    loading.value.yearly = true
    error.value.yearly = ''
    leaderboards.value.yearly = await fetchLeaderboard('yearly')
  } catch (e) {
    error.value.yearly = 'Failed to load leaderboard'
  } finally {
    loading.value.yearly = false
  }
}

const leaderboards = ref<LeaderboardState>({
  monthly: [],
  yearly: [],
  allTime: []
})

const loading = ref<LoadingState>({
  monthly: false,
  yearly: false,
  allTime: false
})

const error = ref<ErrorState>({
  monthly: '',
  yearly: '',
  allTime: ''
})

async function fetchLeaderboard(period: string): Promise<LeaderboardEntry[]> {
  const apiPeriod = period === 'allTime' ? 'all-time' : period
  let url = `${process.env.VUE_APP_API_BASE_URL}/leaderboard?period=${apiPeriod}`
  
  // Add month/year parameters for monthly view
  if (period === 'monthly') {
    url += `&month=${selectedMonth.value + 1}&year=${selectedYear.value}`
  }
  
  // Add year parameter for yearly view
  if (period === 'yearly') {
    url += `&year=${selectedYearly.value}`
  }
  
  const response = await fetch(url, { credentials: 'include' })
  if (!response.ok) {
    throw new Error('Failed to fetch leaderboard')
  }
  return await response.json()
}

async function fetchAllLeaderboards() {
  console.log('Starting fetchAllLeaderboards...')
  // Workspace emoji is already loaded at app level for authenticated users
  
  for (const period of ['monthly', 'yearly', 'allTime']) {
    try {
      loading.value[period] = true
      error.value[period] = ''
      leaderboards.value[period] = await fetchLeaderboard(period)
    } catch (e) {
      error.value[period] = 'Failed to load leaderboard'
    } finally {
      loading.value[period] = false
    }
  }
}

onMounted(fetchAllLeaderboards)
</script>

<style scoped>
.leaderboard-content {
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;
}
</style>
